import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';
import { MtTooltip } from '@components/MtTooltip';

interface AssetCardGridProps {
  onRemove: (id: string) => void;
  onDuplicate: (id: string) => void;
  onRename: (id: string, name: string) => void;
}

export function AssetCardGrid({ onRemove, onDuplicate, onRename }: AssetCardGridProps) {
  const { t } = useTranslation();
  const assets = useAssetLibraryStore((s) => s.assets);
  const selectedAssetId = useAssetLibraryStore((s) => s.selectedAssetId);
  const selectAsset = useAssetLibraryStore((s) => s.selectAsset);

  if (assets.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-xs text-white/30">
        {t('assetLibrary.noModels')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {assets.map((asset) => (
        <ModelCard
          key={asset.id}
          id={asset.id}
          name={asset.name}
          selected={selectedAssetId === asset.id}
          onSelect={() => {
            // 再次点击已选中的模型 → 取消选中
            if (selectedAssetId === asset.id) {
              selectAsset(null);
            } else {
              selectAsset(asset.id);
            }
          }}
          onRemove={() => onRemove(asset.id)}
          onDuplicate={() => onDuplicate(asset.id)}
          onRename={(name) => onRename(asset.id, name)}
        />
      ))}
    </div>
  );
}

interface ModelCardProps {
  id: string;
  name: string;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onRename: (name: string) => void;
}

function ModelCard({ name, selected, onSelect, onRemove, onDuplicate, onRename }: ModelCardProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitRename = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== name) {
      onRename(trimmed);
    } else {
      setEditName(name);
    }
    setEditing(false);
  };

  return (
    <div
      onClick={onSelect}
      className={`group relative flex cursor-pointer flex-col items-center gap-1 rounded-xl border p-2 transition-colors ${
        selected
          ? 'border-white/30 bg-white/10'
          : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/[0.07]'
      }`}
      style={{ minHeight: '100px' }}
    >
      {/* 模型预览占位 */}
      <div className="flex flex-1 items-center justify-center py-1">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`size-8 ${selected ? 'text-white/60' : 'text-white/30'}`}
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
      </div>

      {/* 名称显示/编辑 */}
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') {
              setEditName(name);
              setEditing(false);
            }
          }}
          onBlur={commitRename}
          className="w-full rounded-md border border-white/20 bg-white/10 px-1.5 py-0.5 text-center text-[10px] text-white/80 focus:outline-none"
        />
      ) : (
        <span
          className="w-full truncate text-center text-[10px] text-white/60"
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditName(name);
            setEditing(true);
          }}
        >
          {name}
        </span>
      )}

      {/* 悬浮操作按钮 */}
      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        {/* 复制 */}
        <MtTooltip content={t('assetLibrary.duplicate')} placement="bottom">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            aria-label={t('assetLibrary.duplicate')}
            className="flex h-5 w-5 items-center justify-center rounded bg-white/10 text-white/50 hover:bg-white/20 hover:text-white/80"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-3"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
        </MtTooltip>
        {/* 重命名 */}
        <MtTooltip content={t('assetLibrary.rename')} placement="bottom">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditName(name);
              setEditing(true);
            }}
            aria-label={t('assetLibrary.rename')}
            className="flex h-5 w-5 items-center justify-center rounded bg-white/10 text-white/50 hover:bg-white/20 hover:text-white/80"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-3"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </button>
        </MtTooltip>
        {/* 删除 */}
        <MtTooltip content={t('assetLibrary.delete')} placement="bottom">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            aria-label={t('assetLibrary.delete')}
            className="flex h-5 w-5 items-center justify-center rounded bg-white/10 text-white/50 hover:bg-red-500/30 hover:text-red-300"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-3"
            >
              <path d="M3 6h18" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </MtTooltip>
      </div>
    </div>
  );
}
